import { Formik } from 'formik';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { entityCodeSelector } from '!core/redux/userInfo/userInfo.selectors';
import { TrlProgressBar } from '»components/atoms';
import { uuidSelector } from '»components/Auth';
import {
  onboardingHolderCodeSelector,
  onboardingOpeningRequestIdSelector,
} from '»components/Auth/Onboarding/redux/onboarding.selectors';
import { TrlFormButtons } from '»components/molecules/TrlFormButtons';
import { useCurrentPage, useQuestionnaire } from '»utils/customHooks';
import { TrlFormattedMessagePlain } from '»utils/i18n';

import { FormStyled, QuestionnaireContainer } from './Questionnaire.styles';
import { QuestionnaireOptions } from './QuestionnaireOptions';
import { questionnaireSlice, questionsSelector } from './redux';
import {
  answersQuestionnaireSelector,
  currentStepQuestionnaireSelector,
  isDirtyQuestionnaireSelector,
} from './redux/questionnaire.selectors';

export const Questionnaire = () => {
  const dispatch = useDispatch();

  const questions = useSelector(questionsSelector);
  const currentStep = useSelector(currentStepQuestionnaireSelector);
  const answers = useSelector(answersQuestionnaireSelector);
  const openingRequestId = useSelector(onboardingOpeningRequestIdSelector);
  const onboardingHolderCode = useSelector(onboardingHolderCodeSelector);
  const entityCode = useSelector(entityCodeSelector);
  const uuid = useSelector(uuidSelector);
  const isDirty = useSelector(isDirtyQuestionnaireSelector);

  const { isSettingsQuestionnaire, isSettingsMainPage } = useCurrentPage();
  const { canAnswerQuestInSettings } = useQuestionnaire();

  useEffect(() => {
    if (
      canAnswerQuestInSettings &&
      (isSettingsQuestionnaire || isSettingsMainPage)
    ) {
      dispatch(
        questionnaireSlice.actions.getQuestionnaireRequest({
          openingRequestId,
          holderCode: onboardingHolderCode || entityCode,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAnswerQuestInSettings, uuid]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (isDirty && (isSettingsQuestionnaire || isSettingsMainPage)) {
        event.preventDefault();
        event.returnValue = '';
      }
    };

    const handleUnload = () => {
      if (isDirty && (isSettingsQuestionnaire || isSettingsMainPage))
        dispatch(questionnaireSlice.actions.clearQuestionnaire());
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('unload', handleUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('unload', handleUnload);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty]);

  if (!questions) return null;

  const currentQuestion = questions[currentStep - 1];

  const initialValues =
    answers ||
    questions.reduce((acc, question) => {
      acc[question.questionId] =
        question.multiChoice === 'S'
          ? { value: [], groupId: question.groupId }
          : { value: '', groupId: question.groupId };
      return acc;
    }, {});

  return (
    <Formik enableReinitialize initialValues={initialValues}>
      {({ values }) => {
        const isAdvanceButtonDisabled =
          currentQuestion.mandatory === 'S'
            ? values[currentQuestion.questionId].value === '' ||
              values[currentQuestion.questionId].value.length === 0
            : false;
        const handleClickNext = () => {
          if (currentStep === questions.length) {
            dispatch(
              questionnaireSlice.actions.submitQuestionnaireAnswersRequest({
                openingRequestId,
                holderCode: onboardingHolderCode || entityCode,
                questValues: values,
              }),
            );
          } else {
            dispatch(
              questionnaireSlice.actions.setQuestionnaireCurrentStep({
                direction: 'forward',
              }),
            );
          }
        };
        return (
          <FormStyled isSettingsMainPage={isSettingsMainPage}>
            <QuestionnaireContainer>
              <TrlProgressBar
                title={TrlFormattedMessagePlain({
                  id: 'TRLT_UI-PROGRESS',
                })}
                totalSteps={questions.length}
                currentStep={currentStep}
              />
              <QuestionnaireOptions
                label={currentQuestion.description}
                options={currentQuestion.options}
                isRequired={currentQuestion.mandatory === 'S'}
                multiChoice={currentQuestion.multiChoice === 'S'}
                name={currentQuestion.questionId}
              />
            </QuestionnaireContainer>
            <TrlFormButtons
              fixedAtBottom
              advanceButtonName="TRLT_UI-NEXT"
              isAdvanceButtonDisabled={isAdvanceButtonDisabled}
              handleForwardButtonClick={handleClickNext}
            />
          </FormStyled>
        );
      }}
    </Formik>
  );
};
